import dayjs from "dayjs";
import { httpService } from "./httpService.service";

/**
 * One-field saves for the Site Visit Summary's inline editing.
 *
 * PUT /api/Site/updateSiteVisit merges per property: every existing caller sends
 * only `Id` plus what it actually changed, and the rest of the card survives —
 * see completed/DatasetList/EditDataset.jsx (Id + LinkToDataCollectionResults)
 * and completed/DatasetList/PreviewModal.jsx (Id + Logistic.FulcrumAccount, a
 * partial *nested* update). So an inline edit posts a single field rather than
 * re-sending the whole card, which is both cheaper and safer than a
 * read-modify-write round trip.
 *
 * The property names below are the ones the phase-owning screens already post,
 * so the summary writes exactly what those forms write:
 *   - planning       → planning/VisitBoard/Modals/EditSiteVisitCardModal.jsx
 *   - implementation → implementation/VisitBoard/TableView/modals/Edit.jsx
 *   - reporting      → reporting/ReportingList/modals/Edit.jsx
 *
 * Status is the exception: it has its own endpoint, the same one the Kanban
 * boards use for drag-to-change, so status transitions keep whatever
 * server-side side effects that endpoint owns.
 */

// Every date the site visit API takes is posted in this shape.
const apiDate = (date) => dayjs(date).format("YYYY-MM-DD HH:mm:ss");

const putSiteVisitFields = (id, fields) =>
  new Promise((resolve, reject) => {
    if (!id) {
      reject(new Error("Missing site visit id."));
      return;
    }
    const formData = new FormData();
    formData.append("Id", id);
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value === null || value === undefined ? "" : value);
    });

    httpService.put("/api/Site/updateSiteVisit", formData, resolve, reject, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  });

export const saveSiteVisitName = (id, name) =>
  putSiteVisitFields(id, { Name: name });

export const saveSiteVisitFacility = (id, facilityId) =>
  putSiteVisitFields(id, { FacilityId: facilityId });

export const saveSiteVisitVirtual = (id, isVirtual) =>
  putSiteVisitFields(id, { isVirtual: !!isVirtual });

export const saveSiteVisitCluster = (id, clusterId) =>
  putSiteVisitFields(id, { ClusterId: clusterId });

export const saveSiteVisitCoordinator = (id, coordinatorId) =>
  putSiteVisitFields(id, { "Logistic.TPMCoordinatorId": coordinatorId });

export const saveSiteVisitTeam = (id, teamId) =>
  putSiteVisitFields(id, { "Logistic.TeamId": teamId });

export const saveSiteVisitCoordinationType = (id, coordinationType) =>
  putSiteVisitFields(id, { "Logistic.CoordinationType": coordinationType });

export const saveSiteVisitPostponed = (id, isPostponed) =>
  putSiteVisitFields(id, { "Logistic.IsPostponed": !!isPostponed });

export const saveSiteVisitPostponedReason = (id, reason) =>
  putSiteVisitFields(id, { "Logistic.PostponedReason": reason ?? "" });

export const saveSiteVisitFulcrumAccount = (id, account) =>
  putSiteVisitFields(id, { "Logistic.FulcrumAccount": account ?? "" });

// The Edit Site Visit modal posts this exact shape, so the backend gets the
// planned visit in the format it already parses.
export const saveSiteVisitPlannedVisit = (id, date) =>
  putSiteVisitFields(id, { "Visit.PlannedVisit": apiDate(date) });

export const saveSiteVisitNotes = (id, notes) =>
  putSiteVisitFields(id, { "Logistic.Notes": notes ?? "" });

/* ------------------------------------------------------------------ *
 * Planning
 * ------------------------------------------------------------------ */

export const saveSiteVisitTeamLocal = (id, isTeamLocal) =>
  putSiteVisitFields(id, { "Logistic.IsTeamLocal": !!isTeamLocal });

export const saveSiteVisitActivationDate = (id, date) =>
  putSiteVisitFields(id, { "Logistic.ActivationDate": apiDate(date) });

export const saveSiteVisitPlanApproval = (id, approval) =>
  putSiteVisitFields(id, { "Visit.VisitPlanApproval": approval ?? "" });

export const saveSiteVisitSpecifics = (id, specifics) =>
  putSiteVisitFields(id, { SpecificsOfOnSiteOperation: specifics ?? "" });

// Interview targets. `ClientsCount` is the KII figure — the planning form labels
// it "kills"/KII contacts and posts it under that name.
export const saveSiteVisitBeneficiariesTarget = (id, count) =>
  putSiteVisitFields(id, {
    "Visit.InterviewDetails.BeneficiariesCount": count ?? 0,
  });

export const saveSiteVisitKiiTarget = (id, count) =>
  putSiteVisitFields(id, { "Visit.InterviewDetails.ClientsCount": count ?? 0 });

export const saveSiteVisitObservationTarget = (id, count) =>
  putSiteVisitFields(id, {
    "Visit.InterviewDetails.ObservationChecklist": count ?? 0,
  });

export const saveSiteVisitMediaLimit = (id, limit) =>
  putSiteVisitFields(id, { "Visit.InterviewDetails.MediaLimit": limit ?? 0 });

export const saveSiteVisitSamplingInstructions = (id, value) =>
  putSiteVisitFields(id, {
    "Visit.InterviewDetails.SamplingInstructions": value ?? "",
  });

export const saveSiteVisitClientInstructions = (id, value) =>
  putSiteVisitFields(id, {
    "Visit.InterviewDetails.ClientInstructions": value ?? "",
  });

export const saveSiteVisitOtherInstructions = (id, value) =>
  putSiteVisitFields(id, {
    "Visit.InterviewDetails.OtherInstructions": value ?? "",
  });

/* ------------------------------------------------------------------ *
 * Implementation
 * ------------------------------------------------------------------ */

export const saveSiteVisitPostponedDetails = (id, details) =>
  putSiteVisitFields(id, { "Logistic.PostponedDetails": details ?? "" });

// Tracking fields ride on the site visit id, not the tracking id: the
// implementation Edit form posts them as `Tracking.*` on updateSiteVisit.
export const saveTrackingReportingMonth = (id, month) =>
  putSiteVisitFields(id, { "Tracking.ReportingMonth": month });

export const saveTrackingReportingYear = (id, year) =>
  putSiteVisitFields(id, { "Tracking.ReportingYear": year });

export const saveTrackingTrainingCompleted = (id, completed) =>
  putSiteVisitFields(id, { "Tracking.TrainingIsCompleted": !!completed });

export const saveTrackingTrainingDate = (id, date) =>
  putSiteVisitFields(id, { "Tracking.TrainingDate": apiDate(date) });

export const saveTrackingTrainingScoreInfo = (id, info) =>
  putSiteVisitFields(id, { "Tracking.TrainingScoreInfo": info ?? "" });

export const saveTrackingActualDateOfVisit = (id, date) =>
  putSiteVisitFields(id, { "Tracking.ActualDateOfVisit": apiDate(date) });

export const saveTrackingPercentageCompleted = (id, percentage) =>
  putSiteVisitFields(id, {
    "Tracking.PercentageOfDataCollectionCompleted": percentage ?? 0,
  });

/* ------------------------------------------------------------------ *
 * Reporting
 * ------------------------------------------------------------------ */

export const saveTrackingDateOfReport = (id, date) =>
  putSiteVisitFields(id, { "Tracking.DateOfReport": apiDate(date) });

export const saveTrackingDateOfDebrief = (id, date) =>
  putSiteVisitFields(id, { "Tracking.DateOfDebrief": apiDate(date) });

export const saveSiteVisitDataCollectionLink = (id, link) =>
  putSiteVisitFields(id, { LinkToDataCollectionResults: link ?? "" });

// Required-media flag and count move together, exactly as the reporting form
// couples them: a count above zero means the media is required, and clearing the
// requirement zeroes the count. Sending both in one request keeps the pair from
// ever landing half-updated.
export const saveSiteVisitPhotos = (id, { required, count }) =>
  putSiteVisitFields(id, {
    HasPhotos: !!required,
    PhotoCount: required ? count ?? 0 : 0,
  });

export const saveSiteVisitVideos = (id, { required, count }) =>
  putSiteVisitFields(id, {
    HasVideos: !!required,
    VideoCount: required ? count ?? 0 : 0,
  });

export const saveSiteVisitStatus = (id, status) =>
  new Promise((resolve, reject) => {
    if (!id || status === null || status === undefined) {
      reject(new Error("Missing site visit id or status."));
      return;
    }
    httpService.put(
      `/api/Site/changeSiteVisitStatus?SiteVisitId=${id}&SiteVisitStatus=${status}`,
      "",
      resolve,
      reject
    );
  });

// Field report status hangs off the tracking record, not the site visit, so it
// takes the tracking id — same endpoint the reporting Kanban drags against.
export const saveFieldTeamReportingStatus = (trackingId, status) =>
  new Promise((resolve, reject) => {
    if (!trackingId || status === null || status === undefined) {
      reject(new Error("This site visit has no tracking record yet."));
      return;
    }
    httpService.put(
      `/api/Site/changeFieldTeamReportingStatus?TrackingId=${trackingId}&FieldTeamReportingStatus=${status}`,
      "",
      resolve,
      reject
    );
  });

// httpService hands back either an API error envelope or an axios error.
export const inlineUpdateErrorText = (error, fallback) =>
  error?.errors?.[0]?.message ||
  error?.response?.data?.errors?.[0]?.message ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;
